// Accueil PREMIUM (refonte 16/07, demande Claude-Alain) : overlay DOM qui remplace le menu
// texte peint sur canvas. Fond = les fonds de biomes du jeu (drawMenuBackdrop) en ROTATION
// avec fondu — l'accueil fait visiter les 8 mondes ; devant, titre doré animé (CSS), tuiles
// d'action en cascade, footer discret et panneau Réglages intégré (Dash/son/avatar/torche/CRT).
// Même contrat que makeMenu (MenuHandle avec destroy()) pour l'état `menu` de main.ts.
import type { MeResponse } from '../shared/api-types'
import { WORLD_KEYS } from '../shared/worlds'
import { apiGhostRivals, apiJournal, apiMineState } from './api'
import { isMuted, toggleMute } from './audio'
import { mountCoinBadge, openQuests, showToast } from './economy-ui'
import { renderJournal } from './journal-ui'
import { startVestiaire } from './vestiaire-ui'
import { isTouch } from './platform'
import {
  AVATAR_HUES, avatarHue, avatarIndex, CAM_ZOOM_MAX, CAM_ZOOM_MIN, CAM_ZOOM_STEP, cameraZoom, crtPref, dashPref,
  ghostPref, ghostRival, setAvatarHue, setCameraZoom, setCrtPref, setDashPref, setGhostPref, setGhostRival, setSkinId,
  setTorchPref, skinId, torchPref,
} from './prefs'
import { apiSetSkin } from './api'
import { RUNNER_CELL, SKIN_IDS, SKIN_LABELS, hueIndex, runnerRef, skinIndex, type SkinId } from '../shared/skins'
import { RUNNER_POSE, runnersSheet } from './sprites'
import { drawMenuBackdrop, type RenderCtx } from './render'
import type { MenuHandle } from './screens'

export interface HomeActions {
  onPlay(): void
  onPick(): void
  onLeaderboard(): void
  // Réservées au joueur CONNECTÉ (créer/publier/jouer en ligne exigent une session).
  onOnline?(): void
  onCampaigns?(): void
  onMyLevels?(): void
  onLogout?(): void
  // La Mine aux briques (Lot C) : ouvre la scène jouable. Optionnelle (repli toast si absente).
  onMine?(): void
}

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T

/** Dessine un skin (pose, teinte) de la feuille runners.png sur un canvas, net et pixelisé. */
function drawSkinOn(cv: HTMLCanvasElement, skinIdx: number, hueIdx: number, poseIdx: number): void {
  const img = runnersSheet()
  const c = cv.getContext('2d')!
  c.imageSmoothingEnabled = false
  c.clearRect(0, 0, cv.width, cv.height)
  if (!img) return
  const { sx, sy } = runnerRef(skinIdx, hueIdx, poseIdx)
  c.drawImage(img, sx, sy, RUNNER_CELL, RUNNER_CELL, 0, 0, cv.width, cv.height)
}

/** Le vestiaire (skins cartoon 19/07) : galerie des 10 explorateurs + 7 teintes + aperçu animé.
 *  Choix écrit dans les prefs locales ET envoyé au compte (best-effort hors-ligne). */
function openSkinPicker(onDone: () => void): void {
  let selSkin = skinIndex(skinId())
  let selHue = hueIndex(avatarHue())
  const overlay = document.createElement('div')
  overlay.style.cssText = 'position:fixed;inset:0;z-index:60;background:rgba(8,6,20,.78);display:flex;align-items:center;justify-content:center;padding:12px'
  const panel = document.createElement('div')
  panel.style.cssText = 'background:#161230;border:1px solid #35305e;border-radius:14px;padding:14px 14px 12px;max-width:420px;width:100%;max-height:92vh;overflow:auto;font-family:inherit;color:#f0e9d2'
  overlay.appendChild(panel)
  const title = document.createElement('div')
  title.textContent = 'Ton coureur'
  title.style.cssText = 'font-weight:700;font-size:18px;margin-bottom:8px;color:#ffd23f'
  panel.appendChild(title)
  // Aperçu animé (course 2 frames) + nom du skin sélectionné.
  const preview = document.createElement('canvas')
  preview.width = 96; preview.height = 96
  preview.style.cssText = 'display:block;margin:0 auto;width:96px;height:96px;image-rendering:pixelated'
  const name = document.createElement('div')
  name.style.cssText = 'text-align:center;margin:4px 0 10px;font-size:14px;opacity:.9'
  panel.append(preview, name)
  let frame = 0
  const paintPreview = (): void => drawSkinOn(preview, selSkin, selHue, frame === 0 ? RUNNER_POSE.run[0] : RUNNER_POSE.run[1])
  const timer = window.setInterval(() => { frame = 1 - frame; paintPreview() }, 250)
  // Galerie des 10 (pose idle, à la teinte courante).
  const gallery = document.createElement('div')
  gallery.style.cssText = 'display:grid;grid-template-columns:repeat(5,1fr);gap:6px;margin-bottom:10px'
  panel.appendChild(gallery)
  const galleryCvs: HTMLCanvasElement[] = []
  const paintGallery = (): void => galleryCvs.forEach((cv, i) => drawSkinOn(cv, i, selHue, RUNNER_POSE.idle))
  // Rangée des 7 teintes (le skin sélectionné, décliné).
  const hueRow = document.createElement('div')
  hueRow.style.cssText = 'display:grid;grid-template-columns:repeat(7,1fr);gap:5px;margin-bottom:12px'
  panel.appendChild(hueRow)
  const hueCvs: HTMLCanvasElement[] = []
  const paintHues = (): void => hueCvs.forEach((cv, h) => drawSkinOn(cv, selSkin, h, RUNNER_POSE.idle))
  const refreshMarks = (): void => {
    name.textContent = SKIN_LABELS[SKIN_IDS[selSkin] as SkinId]
    galleryCvs.forEach((cv, i) => { cv.style.outline = i === selSkin ? '2px solid #ffd23f' : '1px solid #35305e' })
    hueCvs.forEach((cv, h) => { cv.style.outline = h === selHue ? '2px solid #ffd23f' : '1px solid #35305e' })
  }
  for (let i = 0; i < SKIN_IDS.length; i++) {
    const cv = document.createElement('canvas')
    cv.width = 64; cv.height = 64
    cv.style.cssText = 'width:100%;aspect-ratio:1;image-rendering:pixelated;border-radius:8px;background:#0d0b1f;cursor:pointer'
    cv.addEventListener('click', () => { selSkin = i; paintPreview(); paintHues(); refreshMarks() })
    gallery.appendChild(cv)
    galleryCvs.push(cv)
  }
  for (let h = 0; h < AVATAR_HUES.length; h++) {
    const cv = document.createElement('canvas')
    cv.width = 48; cv.height = 48
    cv.style.cssText = 'width:100%;aspect-ratio:1;image-rendering:pixelated;border-radius:8px;background:#0d0b1f;cursor:pointer'
    cv.addEventListener('click', () => { selHue = h; paintPreview(); paintGallery(); refreshMarks() })
    hueRow.appendChild(cv)
    hueCvs.push(cv)
  }
  // Valider / fermer.
  const ok = document.createElement('button')
  ok.type = 'button'
  ok.textContent = 'Valider'
  ok.style.cssText = 'display:block;width:100%;padding:10px;border-radius:10px;border:0;background:#ffd23f;color:#221b04;font-weight:700;font-size:15px;cursor:pointer'
  panel.appendChild(ok)
  const close = (): void => { window.clearInterval(timer); overlay.remove() }
  ok.addEventListener('click', () => {
    setSkinId(SKIN_IDS[selSkin]!)
    setAvatarHue(AVATAR_HUES[selHue]!)
    void apiSetSkin(SKIN_IDS[selSkin]!, AVATAR_HUES[selHue]!)
    close()
    onDone()
  })
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })
  document.body.appendChild(overlay)
  paintPreview(); paintGallery(); paintHues(); refreshMarks()
}

export function startHome(opts: {
  sheet: HTMLImageElement
  me: MeResponse | null
  offline: boolean
  actions: HomeActions
}): MenuHandle {
  const { me, offline, actions } = opts
  // Faux dès qu'on quitte l'accueil (destroy) : bloque le montage tardif du badge pièces si
  // apiMineState résout APRÈS le départ (même garde que showLevelPicker dans main.ts).
  let active = true
  const root = $('home')
  const grid = $('hm-grid')
  const hello = $('hm-hello')
  const foot = $('hm-foot')
  const settings = $('hm-settings')
  const settingsList = $('hs-list')

  // --- Tuiles d'action : le FRÉQUENT devant (jouer, cartes, en ligne...), le rare au footer.
  interface Tile { ico: string; label: string; sub: string; run: () => void; hero?: boolean }
  const tiles: Tile[] = [
    { ico: '▶', label: 'Jouer', sub: "Commencer l'aventure", run: actions.onPlay, hero: true },
    { ico: '🗺️', label: 'Cartes', sub: 'Choisir un niveau', run: actions.onPick },
  ]
  if (actions.onOnline) tiles.push({ ico: '⚔️', label: 'En ligne', sub: 'Versus et coop', run: actions.onOnline })
  if (actions.onCampaigns) tiles.push({ ico: '🏰', label: 'Campagnes', sub: 'Enchaîne 5 niveaux', run: actions.onCampaigns })
  if (actions.onMyLevels) tiles.push({ ico: '🛠️', label: 'Créer', sub: 'Ton éditeur de cartes', run: actions.onMyLevels })
  // La Mine aux briques (Lot C) : ouvre la scène jouable si câblée (repli toast sinon).
  tiles.push({ ico: '⛏️', label: 'La Mine', sub: 'Fabrique des briques', run: () => actions.onMine ? actions.onMine() : showToast('La Mine ouvre très bientôt !') })
  // LE VESTIAIRE (26/07) : il était atteignable UNIQUEMENT depuis la Mine, alors que c'est l'écran
  // de son propre personnage. Les couleurs sont gratuites, donc la tuile est là pour tout le monde,
  // connecté ou non (hors ligne, on garde ses couleurs et la tenue d'origine).
  tiles.push({ ico: '🎽', label: 'Vestiaire', sub: 'Ton héros et ses tenues', run: () => { startVestiaire({ onClose: () => { /* rien à rafraîchir : l'accueil ne montre pas le héros */ } }) } })
  tiles.push({ ico: '🎯', label: 'Quêtes', sub: 'Défis à récompenses', run: () => openQuests(me?.pseudo) })
  tiles.push({ ico: '🏆', label: 'Classement', sub: 'Records de la famille', run: actions.onLeaderboard })
  tiles.push({ ico: '⚙️', label: 'Réglages', sub: 'Dash, son, avatar...', run: () => openSettings() })

  grid.textContent = ''
  for (const [i, t] of tiles.entries()) {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = t.hero ? 'hm-tile hm-tile-hero' : 'hm-tile'
    b.style.setProperty('--i', String(i))
    const ico = document.createElement('span')
    ico.className = 'hm-ico'
    ico.textContent = t.ico
    const lab = document.createElement('span')
    lab.className = 'hm-lab'
    lab.textContent = t.label
    const sub = document.createElement('small')
    sub.textContent = t.sub
    lab.appendChild(sub)
    b.append(ico, lab)
    b.addEventListener('click', t.run)
    grid.appendChild(b)
  }

  hello.textContent = me ? `Bonjour ${me.pseudo} 👋` : ''
  foot.textContent = ''
  if (me && actions.onLogout) {
    foot.append(`Connecté : ${me.pseudo} · `)
    const out = document.createElement('button')
    out.type = 'button'
    out.textContent = 'Se déconnecter'
    out.addEventListener('click', actions.onLogout)
    foot.appendChild(out)
  } else if (offline) {
    foot.textContent = 'Hors ligne (les scores ne sont pas enregistrés)'
  }

  // Badge pièces (Lot A) : l'accueil est un overlay STATIQUE réutilisé (destroy() ne vide pas
  // hm-head), donc on retire d'abord un éventuel badge d'un montage précédent (pas d'empilement
  // ni de solde périmé si le rechargement échoue), puis on le (re)charge UNIQUEMENT si connecté,
  // en SILENCE hors ligne / erreur. Le garde `active` évite un montage tardif sur un accueil quitté.
  hello.parentElement?.querySelector('.hm-coins')?.remove()
  if (me) {
    apiMineState()
      .then((state) => { const head = hello.parentElement; if (active && head) mountCoinBadge(head, state.wallet) })
      .catch(() => { /* hors ligne / erreur : pas de badge, jamais bloquant */ })
  }

  // JOURNAL « pendant ton absence » (25/07) : la première chose qu'on doit lire en arrivant. Un
  // seul appel par montage d'accueil — la route CONSOMME sa fenêtre côté serveur, la rappeler en
  // boucle effacerait les nouvelles sans les montrer. Panneau masqué par défaut : hors ligne, non
  // connecté ou « rien de neuf », l'accueil reste exactement ce qu'il était.
  const journal = $('hm-journal')
  journal.hidden = true
  journal.textContent = ''
  if (me) {
    apiJournal()
      .then((data) => { if (active) renderJournal(journal, data) })
      .catch(() => { /* hors ligne / erreur : pas de journal, jamais bloquant */ })
  }

  // --- Panneau Réglages : reconstruit à l'ouverture (chaque ligne reflète l'état courant).
  function buildSettings(): void {
    settingsList.textContent = ''
    const row = (label: string, sub: string, control: HTMLElement): void => {
      const r = document.createElement('div')
      r.className = 'hs-row'
      const lab = document.createElement('span')
      lab.className = 'hs-lab'
      lab.textContent = label
      const small = document.createElement('small')
      small.textContent = sub
      lab.appendChild(small)
      r.append(lab, control)
      settingsList.appendChild(r)
    }
    const toggle = (get: () => boolean, set: (v: boolean) => void): HTMLInputElement => {
      const sw = document.createElement('input')
      sw.type = 'checkbox'
      sw.className = 'hs-switch'
      sw.checked = get()
      sw.addEventListener('change', () => set(sw.checked))
      return sw
    }
    // Jauge « Zoom caméra » (21/07) : de recul max (carte entière) à zoom max ; libellé live en « +X % ».
    const zoomSlider = (): HTMLElement => {
      const wrap = document.createElement('div')
      wrap.className = 'hs-range-wrap'
      const range = document.createElement('input')
      range.type = 'range'
      range.className = 'hs-range'
      range.min = String(CAM_ZOOM_MIN); range.max = String(CAM_ZOOM_MAX); range.step = String(CAM_ZOOM_STEP)
      range.value = String(cameraZoom())
      const val = document.createElement('span')
      val.className = 'hs-range-val'
      const label = (z: number): string => '+' + Math.round((z - 1) * 100) + ' %'
      val.textContent = label(cameraZoom())
      range.addEventListener('input', () => { const z = Number(range.value); setCameraZoom(z); val.textContent = label(z) })
      wrap.append(range, val)
      return wrap
    }
    row('Dash', 'Esquive rapide (double appui)', toggle(dashPref, setDashPref))
    // Son : la pref est « muet » ; l'interrupteur montre « son actif » -> on inverse les deux sens.
    row('Son', 'Musiques d\'ambiance et effets', toggle(() => !isMuted(), (v) => { if (v === isMuted()) toggleMute() }))
    // Héros unique « Le Baroudeur » (19/07) : plus de choix de personnage/couleur (il est figé
    // en ocre, son identité). L'ancien vestiaire openSkinPicker reste en dormance pour un futur
    // 2e perso. La ligne « Personnage » des Réglages est donc retirée.
    // ADVERSAIRE (26/07) : contre QUI le fantôme te fait courir. La liste vient du serveur — les
    // joueurs qui ont vraiment des courses enregistrées, avec sur combien de cartes, parce que
    // choisir quelqu'un qu'on ne croisera jamais est pire que de ne pas choisir. Hors ligne, il
    // reste « Automatique » et le choix déjà mémorisé : jamais une liste vide qui ferait croire
    // que la famille n'a rien couru.
    const rivalSelect = (): HTMLElement => {
      const sel = document.createElement('select')
      sel.className = 'hs-select'
      sel.setAttribute('aria-label', 'Adversaire du fantôme')
      const opt = (value: string, label: string): void => {
        const o = document.createElement('option')
        o.value = value
        o.textContent = label
        sel.appendChild(o)
      }
      const courant = ghostRival()
      const base = (): void => {
        sel.textContent = ''
        opt('', 'Automatique')
      }
      base()
      if (courant) opt(courant, courant) // avant la réponse du serveur, le choix reste lisible
      sel.value = courant
      sel.addEventListener('change', () => setGhostRival(sel.value))
      apiGhostRivals()
        .then((rivals) => {
          base()
          for (const r of rivals) opt(r.pseudo, `${r.pseudo} · ${r.count} carte${r.count > 1 ? 's' : ''}`)
          // Un choix mémorisé qui n'a plus aucune course reste AFFICHÉ (et non repoussé en silence
          // sur « Automatique ») : le joueur doit voir ce qu'il a demandé, même si c'est vide.
          if (courant && !rivals.some((r) => r.pseudo === courant)) opt(courant, `${courant} · aucune course`)
          sel.value = courant
        })
        .catch(() => { /* hors ligne : « Automatique » + le choix mémorisé suffisent */ })
      return sel
    }
    // FANTÔME (25/07) : par défaut ACTIF. Il ne s'affiche que sur une carte déjà finie par
    // quelqu'un, donc l'option ne « pollue » jamais une carte neuve.
    row('Fantôme', 'Un autre joueur court à côté de toi', toggle(ghostPref, setGhostPref))
    row('Adversaire', 'Le fantôme contre qui tu cours', rivalSelect())
    row('Torche', 'Halo de lumière autour du coureur', toggle(torchPref, setTorchPref))
    row('Écran CRT', 'Effet télé rétro à balayage', toggle(crtPref, setCrtPref))
    row('Zoom caméra', 'Recul (carte entière) ↔ zoom (perso plus gros)', zoomSlider())
  }
  function openSettings(): void { buildSettings(); settings.hidden = false }
  function closeSettings(): void { settings.hidden = true }
  const closeBtn = $('hs-close')
  closeBtn.addEventListener('click', closeSettings)
  const onSettingsBackdrop = (e: MouseEvent): void => { if (e.target === settings) closeSettings() }
  settings.addEventListener('click', onSettingsBackdrop)

  // --- Fond d'ambiance : biome de départ au hasard, puis rotation (HOLD) avec fondu (FADE).
  // Le biome suivant est peint sur un canvas TAMPON puis blitté en alpha : un globalAlpha posé
  // directement serait écrasé par les alphas internes du décor (particules à pleine force).
  const bg = $<HTMLCanvasElement>('hm-bg')
  const bgCtx = bg.getContext('2d')!
  bgCtx.imageSmoothingEnabled = false
  const rc: RenderCtx = { ctx: bgCtx, sheet: opts.sheet }
  const fadeCv = document.createElement('canvas')
  fadeCv.width = bg.width
  fadeCv.height = bg.height
  const fadeCtx = fadeCv.getContext('2d')!
  const fadeRc: RenderCtx = { ctx: fadeCtx, sheet: opts.sheet }
  const HOLD = 16000, FADE = 1800 // ms par biome, durée du fondu
  const firstWorld = Math.floor(Math.random() * WORLD_KEYS.length)
  const t0 = performance.now()
  function paint(now: number): void {
    const t = (now - t0) * 0.03 // temps décoratif ~ticks (30/s), rien de simulé ici
    const step = Math.floor((now - t0) / HOLD)
    const world = WORLD_KEYS[(firstWorld + step) % WORLD_KEYS.length]!
    const next = WORLD_KEYS[(firstWorld + step + 1) % WORLD_KEYS.length]!
    drawMenuBackdrop(rc, world, t)
    const phase = (now - t0) % HOLD
    if (phase > HOLD - FADE) {
      drawMenuBackdrop(fadeRc, next, t)
      bgCtx.save()
      bgCtx.globalAlpha = (phase - (HOLD - FADE)) / FADE
      bgCtx.drawImage(fadeCv, 0, 0)
      bgCtx.restore()
    }
  }
  let raf = 0
  let last = 0
  function loop(now: number): void {
    raf = requestAnimationFrame(loop)
    if (now - last < 33) return // ~30 im/s : fluide et économe (batterie du téléphone)
    last = now
    paint(now)
  }
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) paint(performance.now())
  else raf = requestAnimationFrame(loop)

  // --- Clavier : flèches pour circuler entre les tuiles, Entrée native, Échap ferme Réglages.
  function onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape' && !settings.hidden) { e.preventDefault(); closeSettings(); return }
    if (!settings.hidden) return // panneau ouvert : pas de navigation de tuiles derrière
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) return
    const btns = [...grid.querySelectorAll<HTMLButtonElement>('.hm-tile')]
    if (btns.length === 0) return
    const cur = btns.indexOf(document.activeElement as HTMLButtonElement)
    const delta = e.code === 'ArrowUp' || e.code === 'ArrowLeft' ? -1 : 1
    const nextIdx = cur < 0 ? 0 : Math.max(0, Math.min(btns.length - 1, cur + delta))
    btns[nextIdx]!.focus()
    e.preventDefault()
  }
  addEventListener('keydown', onKey)

  // Crochet de dev (captures headless) : ?settings ouvre le panneau au montage.
  if (/[?&]settings\b/.test(location.search)) openSettings()

  root.hidden = false
  // Desktop : le focus part sur « Jouer » (flèches + Entrée, comme l'ancien menu). Pas au
  // tactile : un focus posé d'office y déclenche des styles de survol trompeurs.
  if (!isTouch) grid.querySelector<HTMLButtonElement>('.hm-tile')?.focus()

  return {
    destroy(): void {
      active = false // coupe le montage tardif du badge pièces (apiMineState en vol)
      cancelAnimationFrame(raf)
      removeEventListener('keydown', onKey)
      closeBtn.removeEventListener('click', closeSettings)
      settings.removeEventListener('click', onSettingsBackdrop)
      settings.hidden = true
      journal.hidden = true
      journal.textContent = ''
      grid.textContent = ''
      root.hidden = true
    },
  }
}
